import React, { useCallback, useMemo } from 'react';

const dataSourceType = [
    { value: 'LocalFile', label: 'Файл на сервере' },
    { value: 'Pop3', label: 'Email (POP3)' }
];

interface FileConfiguration {
    FilePath: string;
}

interface Pop3Configuration
{
    Host: string;
    Port: number;
    Username: string;
    Password: string;
    UseSsl: boolean;
    SenderEmail: string;
}

interface DataSourceConfigurationProps {
    configuration: {
        // IDataSourceConfiguration Members
        SourceType?: string;
        Configuration?: string;
    };
    onChange: (field: string, value: string | number | boolean) => void;
    disabled?: boolean;
}

interface DataSourceConfigurationComponent extends React.FC<DataSourceConfigurationProps> {
    configFields: string[];
}

const DataSourceConfiguration: DataSourceConfigurationComponent = ({
    configuration,
    onChange,
    disabled = false
}) => {
    const cfg = useMemo<FileConfiguration | Pop3Configuration | Record<string, unknown>>(() => {
        try {
            return JSON.parse(configuration.Configuration ?? '{}');
        } catch {
            return {};
        }
    }, [configuration.Configuration]);

    const onLocalChange = useCallback((field: string, value: string | number | boolean) => {
        console.log(field, value);
        const newCfg = {
            ...cfg,
            [field]: value
        };

        const configurationValue = JSON.stringify(newCfg);
        onChange('Configuration', configurationValue);
    }, [cfg, onChange]);

    return (
        <React.Fragment>
            <div className="form-group-custom">
                <label className="form-label-custom">
                    Источник
                </label>
                <div className="input-group-custom">
                    <select
                        className="form-select-custom"
                        value={configuration.SourceType}
                        onChange={(e) => {
                            onChange('SourceType', e.target.value);
                        }}
                        disabled={disabled}
                    >
                        <option value="">Выберите источник</option>
                        {dataSourceType.map(sourceType => (
                            <option key={sourceType.value} value={sourceType.value}>
                                {sourceType.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {configuration.SourceType === 'Pop3' && (
                <>
                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">🔒</span>
                            Адрес сервера POP3
                        </label>
                        <div className="input-group-custom">
                            <input
                                type="text"
                                className="form-input-custom"
                                value={(cfg as Pop3Configuration)?.Host || ''}
                                disabled={disabled}
                                onChange={(e) => onLocalChange('Host', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">🔒</span>
                            Порт POP3
                        </label>
                        <div className="input-group-custom">
                            <input
                                type="number"
                                className="form-input-custom"
                                value={(cfg as Pop3Configuration)?.Port || 995}
                                placeholder="995"
                                disabled={disabled}
                                onChange={(e) => onLocalChange('Port', parseInt(e.target.value) || 995)}
                            />
                        </div>
                    </div>

                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">👤</span>
                            Логин POP3
                        </label>
                        <div className="input-group-custom">
                            <input
                                type="text"
                                className="form-input-custom"
                                value={(cfg as Pop3Configuration)?.Username || ''}
                                placeholder="Введите логин"
                                disabled={disabled}
                                onChange={(e) => onLocalChange('Username', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">🔑</span>
                            Пароль POP3
                        </label>
                        <div className="input-group-custom">
                            <input
                                type="password"
                                className="form-input-custom"
                                value={(cfg as Pop3Configuration)?.Password || ''}
                                placeholder="Введите пароль"
                                disabled={disabled}
                                onChange={(e) => onLocalChange('Password', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">📤</span>
                            Email отправителя
                        </label>
                        <div className="input-group-custom">
                            <input
                                type="email"
                                className="form-input-custom"
                                value={(cfg as Pop3Configuration)?.SenderEmail || ''}
                                placeholder="sender@example.com"
                                disabled={disabled}
                                onChange={(e) => onLocalChange('SenderEmail', e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">🔒</span>
                            Использовать SSL
                        </label>
                        <div className="input-group-custom">
                            <label className="form-check-custom">
                                <input
                                    type="checkbox"
                                    className="form-check-input-custom"
                                    checked={(cfg as Pop3Configuration)?.UseSsl || false}
                                    disabled={disabled}
                                    onChange={(e) => onLocalChange('UseSsl', e.target.checked)}
                                />
                                <span className="form-check-label-custom">Включить SSL</span>
                            </label>
                        </div>
                    </div>
                </>
            )}

            {configuration.SourceType === 'LocalFile' && (
                <>
                    <div className="form-group-custom">
                        <label className="form-label-custom">
                            <span className="label-icon">📁</span>
                            Путь к файлу
                        </label>
                        <div className="input-group-custom">
                            <input
                                type="text"
                                className="form-input-custom"
                                value={(cfg as FileConfiguration)?.FilePath || ''}
                                onChange={(e) => onLocalChange('FilePath', e.target.value)}
                                placeholder="Введите имя файла"
                                disabled={disabled}
                            />
                        </div>
                    </div>
                </>
            )}

        </React.Fragment>
    );
};

// Define the configuration fields used by this supplier
DataSourceConfiguration.configFields = [
    'SourceType',
    'Configuration',
];

export default DataSourceConfiguration;
